import { IPhone, TNullable } from '@/types';
import { TListCountriesItemCamel } from '@/types/list';

/**
 * Formats a phone number with its country code.
 * - Returns `undefined` if phone, country, or number is missing.
 * - Looks up the country code from the provided list of countries.
 * @param phone - The phone object containing `country` and `number`.
 * @param countries - List of countries with `id` and `phonecode`.
 * @returns Formatted phone string in the format `(+<countryCode>) <number>` or `undefined`.
 */
export const formatPhoneNumberWithCode = (
  phone: TNullable<IPhone>,
  countries: TListCountriesItemCamel[],
): string | undefined => {
  if (!phone || !phone.country || !phone.number) return;
  const country = countries.find((c) => c.id === phone.country);
  const phoneCode = country?.phonecode;
  return phoneCode ? `(+${phoneCode}) ${phone.number}` : undefined;
};

/**
 * Masks a phone number while preserving its country code and last digits.
 *
 * The function formats the phone number into a masked representation:
 * `+<countryCode> ******<last4Digits>`
 *
 * Behavior:
 * - Returns `null` if phone, country, or number is missing, or if country code is not found.
 * - If the phone number has fewer than 4 digits, returns it without masking.
 * - Otherwise, replaces all digits except the last 4 with `*`.
 * @param phone - The phone object containing `country` and `number`.
 * @param countries - List of countries with `id` and `phonecode`.
 * @returns Masked phone string in the format `+<countryCode>******1234` or `null`.
 */
export const maskPhoneNumber = (
  phone: TNullable<IPhone>,
  countries: TListCountriesItemCamel[],
) => {
  if (!phone || !phone.country || !phone.number) return null;
  const phoneCode = countries.find((c) => c.id === phone.country)?.phonecode;
  if (!phoneCode) return null;
  if (phone.number.length < 4) return `+${phoneCode} ${phone.number}`;

  const last4 = phone.number.slice(-4);
  const maskedPart = '*'.repeat(Math.max(phone.number.length - 4, 0));
  return `+${phoneCode}${maskedPart}${last4}`;
};
